// @spec SHL-STR-037
import { Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, FileQuestion, Home, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

export function NotFound() {
  const router = useRouter();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Log 404 event on mount for monitoring
  useEffect(() => {
    console.warn('[404]', {
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      referrer: document.referrer || null,
    });
  }, []);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      router.history.back();
    } else {
      void router.navigate({ to: '/' });
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to home for now - can integrate with global search later
      void router.navigate({ to: '/', search: { q: searchQuery } });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4">
            <FileQuestion className="h-16 w-16 text-muted-foreground" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl">{t('errors.pageNotFound')}</CardTitle>
          <CardDescription className="text-base">
            {t('errors.pageNotFoundDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Navigation buttons */}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button variant="outline" onClick={handleGoBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('buttons.goBack')}
            </Button>
            <Button asChild>
              <Link to="/">
                <Home className="mr-2 h-4 w-4" />
                {t('buttons.returnHome')}
              </Link>
            </Button>
          </div>

          {/* Search section */}
          <div className="border-t pt-4">
            <p className="mb-3 text-sm text-muted-foreground">{t('errors.searchPrompt')}</p>
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                type="search"
                placeholder={t('buttons.search')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                className="flex-1"
                aria-label={t('buttons.search')}
              />
              <Button type="submit" size="icon" variant="outline">
                <Search className="h-4 w-4" />
                <span className="sr-only">{t('buttons.search')}</span>
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
